import type { ReactNode } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { ShellHeaderActions, ShellHeaderCrumb, useInShellHeader } from "@/components/layout/ShellHeader"
import {
  ArrowDown01Icon,
  Calendar01Icon,
  Delete01Icon,
  FileDownloadIcon,
  File01Icon,
  PlusSignIcon,
  MoreHorizontalIcon,
} from '@hugeicons/core-free-icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  HEATMAP_PERIODS,
  formatCount,
  formatDeviceShare,
  formatPeriodRange,
  pagePath,
  type DeviceShares,
  type HeatmapDevice,
  type HeatmapMode,
} from './heatmapInsights'

export type HeatmapPageOption = { page_url: string; title?: string }

/**
 * Page and capture actions. Omit a handler to hide its item (share links).
 * The top bar shows page actions; capture history and Recapture render in
 * the capture strip (`HeatmapCaptureMenu`).
 */
export type HeatmapPageMenuActions = {
  onAddPage?: () => void
  /** At the plan limit "Add page" opens the paywall; the label says so. */
  addPageAtLimit?: boolean
  onDownloadHeatmap?: () => void
  onDownloadScrollDetails?: () => void
  onRecapture?: () => void
  onRemovePage?: () => void
  isDownloading?: boolean
  isCapturing?: boolean
  /** Earlier captures of the page, newest first; listed when there is more than one. */
  captures?: Array<{ value: string; label: string }>
  selectedCapture?: string
  onSelectCapture?: (value: string) => void
}

interface HeatmapTopBarProps extends HeatmapPageMenuActions {
  pages: HeatmapPageOption[]
  selectedIndex: number
  onSelectPage: (index: number) => void
  /** Page-wide unique visitors for the period, when the API returned them. */
  visitors: number | null
  mode: HeatmapMode
  onModeChange: (mode: HeatmapMode) => void
  device: HeatmapDevice
  onDeviceChange: (device: HeatmapDevice) => void
  /** Clicks per device on the page; the toggle shows each device's share. */
  deviceShares?: DeviceShares
  period: string
  onPeriodChange: (period: string) => void
  /** Fixed "now" for the range label (the DEV states gallery). */
  now?: Date
}

const CONTROL =
  'inline-flex h-8 shrink-0 items-center rounded-lg bg-card shadow-[0_0_0_1px_var(--input)] transition-colors hover:bg-black/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=open]:bg-black/[0.03] dark:hover:bg-white/5'

function Chevron() {
  return <HugeiconsIcon icon={ArrowDown01Icon} size={12} className="shrink-0 text-foreground/45" />
}

/** Paper segmented control: #F2F2F2 track, white thumb, 26px segments. */
function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: Array<{ value: T; label: ReactNode; title?: string }>
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex shrink-0 rounded-lg bg-black/[0.05] p-0.5 dark:bg-white/[0.06]">
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex h-[26px] items-center rounded-md px-3 text-[12px] leading-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              active
                ? 'bg-card font-medium text-ui-title shadow-[0_1px_2px_rgb(0_0_0/0.08)] dark:bg-white/10'
                : 'text-foreground/60 hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The slim bar above the heatmap (Paper "Heatmaps · Simple · Clicks · 1440"):
 * page picker (path + visitors, switch or add a page) and a page-actions
 * button (downloads, remove) on the left; Clicks | Scroll, Desktop | Mobile
 * and the date range on the right. No left rail.
 */
export function HeatmapTopBar({
  pages,
  selectedIndex,
  onSelectPage,
  visitors,
  mode,
  onModeChange,
  device,
  onDeviceChange,
  deviceShares,
  period,
  onPeriodChange,
  now,
  onAddPage,
  addPageAtLimit,
  onDownloadHeatmap,
  onDownloadScrollDetails,
  onRemovePage,
  isDownloading,
}: HeatmapTopBarProps) {
  const inShell = useInShellHeader()
  const selected = pages[selectedIndex]
  const isPreset = HEATMAP_PERIODS.some((p) => p.value === period)
  const hasPageActions = onDownloadHeatmap || onDownloadScrollDetails || onRemovePage

  const pageMenu = (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Switch page"
          className={cn(CONTROL, 'min-w-0 max-w-full gap-2 px-2.5')}
        >
          <HugeiconsIcon icon={File01Icon} size={13} className="shrink-0 text-foreground/45" />
          <span className="truncate text-[13px] font-medium leading-4 text-ui-title">
            {selected ? pagePath(selected.page_url) : 'Choose a page'}
          </span>
          {visitors !== null && (
            <span className="shrink-0 text-[12px] leading-4 text-muted-foreground">
              {formatCount(visitors)} visitors
            </span>
          )}
          <Chevron />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[300px]">
          <DropdownMenuLabel className="text-[12px] font-normal text-muted-foreground">
            Switch page · {pages.length} tracked
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={String(selectedIndex)} onValueChange={(v) => onSelectPage(Number(v))}>
            {pages.map((page, i) => (
              <DropdownMenuRadioItem key={page.page_url} value={String(i)} className="text-[13px]">
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{pagePath(page.page_url)}</span>
                  {page.title && <span className="truncate text-[12px] text-muted-foreground">{page.title}</span>}
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {onAddPage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onAddPage} className="text-[13px]">
                <HugeiconsIcon icon={PlusSignIcon} size={14} />
                {addPageAtLimit ? 'Add another page · upgrade' : 'Add another page'}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
  )

  // Page actions get their own visible button: inside the page picker they
  // read as page names and nobody found them (beta feedback, 2026-10-23).
  const pageActions = hasPageActions && (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Page actions"
          title="Download or remove this page"
          className={cn(CONTROL, 'w-8 justify-center')}
        >
          <HugeiconsIcon icon={MoreHorizontalIcon} size={14} className="text-foreground/60" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[240px]">
          <DropdownMenuLabel className="truncate text-[12px] font-normal text-muted-foreground">
            {selected ? pagePath(selected.page_url) : 'This page'}
          </DropdownMenuLabel>
          {onDownloadHeatmap && (
            <DropdownMenuItem onSelect={onDownloadHeatmap} disabled={isDownloading} className="text-[13px]">
              <HugeiconsIcon icon={FileDownloadIcon} size={14} />
              Download heatmap image
            </DropdownMenuItem>
          )}
          {onDownloadScrollDetails && (
            <DropdownMenuItem onSelect={onDownloadScrollDetails} disabled={isDownloading} className="text-[13px]">
              <HugeiconsIcon icon={File01Icon} size={14} />
              Download scroll details
            </DropdownMenuItem>
          )}
          {onRemovePage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onRemovePage} variant="destructive" className="text-[13px]">
                <HugeiconsIcon icon={Delete01Icon} size={14} />
                Remove page
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
  )

  const pageControls = (
      <div className="flex min-w-0 items-center gap-1.5">
        {pageMenu}
        {pageActions}
      </div>
  )

  const controls = (
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="Heatmap type"
          value={mode}
          onChange={onModeChange}
          options={[
            { value: 'clicks', label: 'Clicks' },
            { value: 'scroll', label: 'Scroll' },
          ]}
        />
        <Segmented
          label="Device"
          value={device}
          onChange={onDeviceChange}
          options={(['mobile', 'desktop'] as const).map((d) => {
            const share = deviceShares ? formatDeviceShare(deviceShares[d], deviceShares.total) : null
            return {
              value: d,
              label: (
                <>
                  {d === 'mobile' ? 'Mobile' : 'Desktop'}
                  {share && <span className="ml-1.5 tabular-nums text-foreground/45">{share}</span>}
                </>
              ),
              title: share ? `${share} of ${d === 'mobile' ? 'taps' : 'clicks'} on this page come from ${d}` : undefined,
            }
          })}
        />
        <DropdownMenu>
          <DropdownMenuTrigger className={cn(CONTROL, 'gap-1.5 bg-transparent px-2.5')}>
            <HugeiconsIcon icon={Calendar01Icon} size={13} className="shrink-0 text-foreground/45" />
            <span className="whitespace-nowrap text-[12px] font-medium leading-4 text-ui-ink">
              {isPreset ? formatPeriodRange(period, now) : 'Custom range'}
            </span>
            <Chevron />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuRadioGroup value={period} onValueChange={onPeriodChange}>
              {HEATMAP_PERIODS.map((p) => (
                <DropdownMenuRadioItem key={p.value} value={p.value} className="text-[13px]">
                  {p.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
  )

  // Inside the app shell the selector extends the breadcrumb and the controls
  // sit in the header's actions slot: one header row, as in Paper.
  if (inShell) {
    return (
      <>
        <ShellHeaderCrumb>{pageControls}</ShellHeaderCrumb>
        <ShellHeaderActions>{controls}</ShellHeaderActions>
      </>
    )
  }

  return (
    <div className="flex min-h-[52px] flex-wrap items-center gap-2 border-b border-black/5 px-4 py-[9px] sm:px-5 dark:border-white/[0.07]">
      {pageControls}
      <div className="hidden flex-1 sm:block" />
      {controls}
    </div>
  )
}
